import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useFlipperFlag } from './useFlipperFlag'
import {
  hasBlogIntegrationConnected,
  canConnectBlogProvider,
  getConnectedBlogIntegration,
  getBlogIntegrationRestrictionMessage,
} from 'utils/blogIntegrationRestrictions'
import { BlogPlatformIntegration } from 'types'

export const useBlogIntegrations = () => {
  const { data } = useExternalIntegrations()
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const zapierPickerEnabled = useFlipperFlag('zapier_v2')
  const rawIntegrations = data?.external_integrations
  // When the picker is disabled, Zapier integrations exist only as the legacy
  // API-key connection — they're not a blog/email destination. Filter them out
  // here so every downstream caller (icons, "Post now" gating, brand-plan
  // resolution, etc.) treats the workspace as if no Zapier blog exists.
  const integrations = zapierPickerEnabled
    ? rawIntegrations
    : rawIntegrations?.filter((i) => i.provider !== ExternalIntegrationEnum.Zapier)

  if (!integrations) {
    return {
      blogIntegrations: [],
      hasBlogIntegrationConnected: false,
      canConnectWordPress: true,
      canConnectGoHighLevel: true,
      canConnectWix: wixEnabled,
      canConnectShopify: shopifyEnabled,
      connectedBlogIntegration: null,
      getRestrictionMessage: () => null,
    }
  }

  const supportedProviders = [ExternalIntegrationEnum.WordPress, ExternalIntegrationEnum.GoHighLevel]

  if (wixEnabled) {
    supportedProviders.push(ExternalIntegrationEnum.Wix)
  }

  if (shopifyEnabled) {
    supportedProviders.push(ExternalIntegrationEnum.Shopify)
  }

  const blogIntegrations = integrations.filter((integration) => {
    if (supportedProviders.includes(integration.provider)) return true
    // A Zapier integration counts as a blog integration only when explicitly
    // flagged as the workspace's blog destination via the ZapierTargetsPicker.
    if (integration.provider === ExternalIntegrationEnum.Zapier) {
      const additionalData = integration.additional_data as { is_blog_destination?: boolean } | undefined
      return additionalData?.is_blog_destination === true
    }
    return false
  })

  const hasBlogConnected = hasBlogIntegrationConnected(integrations)
  const connectedBlogIntegration = getConnectedBlogIntegration(integrations)
  const canConnectWordPress = canConnectBlogProvider(integrations, ExternalIntegrationEnum.WordPress)
  const canConnectGoHighLevel = canConnectBlogProvider(integrations, ExternalIntegrationEnum.GoHighLevel)
  const canConnectWix = wixEnabled && canConnectBlogProvider(integrations, ExternalIntegrationEnum.Wix)
  const canConnectShopify = shopifyEnabled && canConnectBlogProvider(integrations, ExternalIntegrationEnum.Shopify)

  const getRestrictionMessage = (provider: BlogPlatformIntegration) => {
    return getBlogIntegrationRestrictionMessage(integrations, provider)
  }

  return {
    blogIntegrations,
    hasBlogIntegrationConnected: hasBlogConnected,
    canConnectWordPress,
    canConnectGoHighLevel,
    canConnectWix,
    canConnectShopify,
    connectedBlogIntegration,
    getRestrictionMessage,
  }
}
